<template>
  <v-card class="weather-card" elevation="1" height="100%">
    <v-card-text class="pa-0">
      <div class="weather-card-image">
        <v-icon
          :icon="getWeatherIcon()"
          class="weather-icon"
          size="54"
          color="white"
        ></v-icon>
        
        <div class="weather-overlay">
          <div class="d-flex align-center text-subtitle-2 text-white mb-1">
            <v-icon size="small" class="mr-1">mdi-map-marker</v-icon>
            {{ location }}
          </div>
          
          <div class="weather-temp text-h4 font-weight-medium">
            {{ temperature }}°{{ unit }}
          </div>
        </div>
      </div>
      
      <div class="pt-3 px-4 pb-3">
        <div class="d-flex justify-space-between align-center">
          <div class="text-body-2 font-weight-medium">
            {{ getConditionText() }}
          </div>
          
          <div class="weather-date text-caption text-medium-emphasis">
            {{ getFormattedDate() }}
          </div>
        </div>
        
        <div class="d-flex justify-space-between text-caption text-medium-emphasis mt-2">
          <span>{{ weatherInfo }}</span>
          <div>
            <v-icon size="small" icon="mdi-arrow-up-thin" class="me-n1"></v-icon>
            <span class="me-2">{{ getHighTemp() }}°</span>
            <v-icon size="small" icon="mdi-arrow-down-thin" class="me-n1"></v-icon>
            <span>{{ getLowTemp() }}°</span>
          </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'WeatherWidget',
  props: {
    location: {
      type: String,
      default: 'Paris'
    },
    temperature: {
      type: [Number, String],
      default: '--'
    },
    condition: {
      type: String,
      default: 'clear' // clear, cloudy, partly-cloudy, rainy, snowy, stormy
    },
    weatherInfo: {
      type: String,
      default: 'Qualité de l\'air: Bonne'
    },
    unit: {
      type: String,
      default: 'C'
    }
  },
  data() {
    return {
      // Définition des icônes météo
      weatherIcons: {
        clear: 'mdi-weather-sunny',
        cloudy: 'mdi-weather-cloudy',
        'partly-cloudy': 'mdi-weather-partly-cloudy',
        rainy: 'mdi-weather-pouring',
        snowy: 'mdi-weather-snowy',
        stormy: 'mdi-weather-lightning-rainy'
      },
      
      // Textes des conditions météo
      conditionTexts: {
        clear: 'Ensoleillé',
        cloudy: 'Nuageux',
        'partly-cloudy': 'Partiellement nuageux',
        rainy: 'Pluvieux',
        snowy: 'Neigeux',
        stormy: 'Orageux'
      }
    };
  },
  methods: {
    // Obtenir l'icône de la condition météo
    getWeatherIcon() {
      return this.weatherIcons[this.condition] || 'mdi-weather-partly-cloudy';
    },
    
    // Obtenir le texte de la condition météo
    getConditionText() {
      return this.conditionTexts[this.condition] || 'Partiellement nuageux';
    },
    
    // Obtenir l'icône de la condition pour la partie inférieure
    getConditionIcon() {
      return this.getWeatherIcon();
    },
    
    // Formater la date actuelle
    getFormattedDate() {
      const now = new Date();
      const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
      const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
      
      return `${days[now.getDay()]} ${now.getDate()} ${months[now.getMonth()]}`;
    },
    
    // Générer une température maximale (pour la démonstration)
    getHighTemp() {
      // Si la température est un nombre, ajouter 3-5 degrés
      if (!isNaN(this.temperature)) {
        return Math.min(Number(this.temperature) + 3 + Math.floor(Math.random() * 3), 40);
      }
      return '--';
    },
    
    // Générer une température minimale (pour la démonstration)
    getLowTemp() {
      // Si la température est un nombre, retrancher 3-5 degrés
      if (!isNaN(this.temperature)) {
        return Math.max(Number(this.temperature) - 3 - Math.floor(Math.random() * 3), -10);
      }
      return '--';
    }
  }
};
</script>

<style lang="scss" scoped>
.weather-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 3px 3px -2px rgba(0,0,0,0.2), 
                0 3px 4px 0 rgba(0,0,0,0.14),
                0 1px 8px 0 rgba(0,0,0,0.12) !important;
  }
  
  .weather-card-image {
    position: relative;
    height: 160px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: linear-gradient(to bottom right, #1976D2, #64B5F6);
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: url('https://picsum.photos/400/400?blur=2') center/cover;
      opacity: 0.2;
      z-index: 0;
    }
  }
  
  .weather-icon {
    font-size: 3.5rem;
    z-index: 1;
    margin-bottom: 40px;
    filter: drop-shadow(0 0 8px rgba(255,255,255,0.3));
  }
  
  .weather-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px;
    background: linear-gradient(to top, rgba(0,0,0,0.4), transparent);
    color: white;
    text-shadow: 0 1px 3px rgba(0,0,0,0.3);
    z-index: 1;
  }
  
  .weather-temp {
    font-weight: 500;
  }
  
  .weather-date {
    opacity: 0.8;
  }
}
</style>